/**
 * Arm B's Strip B, "Talk for a minute. Get back what stood out" (#1070), shown
 * right after the Time capsule: four steps from prompt to read-back, each
 * beside a demo phone screen.
 *
 * Built from the approved markup in `Homepage B.dc.html`, section `05 NEW
 * Strip B`, and its `renderVals()` layout table: below 900px the prototype's
 * `L.vertical` (a rail down the left), at 900px and above `L.zigzag` (the rail
 * down the centre, phone and caption swapping sides each step). The
 * stylesheet switches between them; there is no other mode.
 *
 * **Motion** (no library, no timer). The markup is the finished, still state —
 * every step shown, the rail full, every dot solid — which is what a visitor
 * gets before any JavaScript runs and under `prefers-reduced-motion: reduce`.
 * Otherwise, once mounted, the stepper opts into motion: each step fades in
 * and rises 28px the first time it enters view (an IntersectionObserver), and
 * the rail fill tracks scroll progress through the stepper (one passive
 * scroll listener, throttled to animation frames and attached only while the
 * stepper intersects the viewport), turning each dot solid as it reaches it.
 * The motion writes attributes and a custom property React does not manage,
 * so a re-render never resets it.
 */
import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";

import DemoPicture from "./DemoPicture";
import { accent } from "../accent";
import {
  DEMO_NAME,
  RECORDING_DEMO,
  railProgress,
  STEPPER_STEPS,
  STEPPER_ZIGZAG_MIN_WIDTH,
  stepReachedAt,
} from "./homepageDemo";

/** Share of a step that must be in view before it reveals (the prototype's). */
const REVEAL_THRESHOLD = 0.3;

/** `sizes` of a stepper phone: 280px wide in zigzag, at most 250px vertical. */
const PHONE_SIZES = `(min-width: ${STEPPER_ZIGZAG_MIN_WIDTH}px) 280px, 250px`;

/** The pill naming the chosen thread, across the top of the recording screens. */
function ThreadPill() {
  const t = useTranslations("web.homeB.scene.phone");
  return <span className="figma-b__ph-thread">{t("threads.today")}</span>;
}

/** Step 1: the prompt picker, drawn with no photo. */
function PromptScreen() {
  const t = useTranslations("web.homeB.scene.phone");
  return (
    <div className="figma-b__ph figma-b__ph--prompt">
      <span className="figma-b__ph-close">{t("close")}</span>
      <div className="figma-b__ph-prompt">
        <div className="figma-b__ph-prompt-title">{t("promptTitle", { name: DEMO_NAME })}</div>
        <div className="figma-b__ph-prompt-body">{t("promptBody")}</div>
        <div className="figma-b__ph-choice">{t("threads.today")}</div>
        <div className="figma-b__ph-choice">{t("threads.returning")}</div>
        <div className="figma-b__ph-choice">{t("threads.talk")}</div>
      </div>
    </div>
  );
}

/**
 * Step 2: the countdown over the demo person.
 *
 * @param {object} props
 * @param {import("./homepageDemo").DemoScene} props.scene - the photo behind it.
 */
function CountdownScreen({ scene }) {
  const t = useTranslations("web.homeB.scene.phone");
  return (
    <div className="figma-b__ph figma-b__ph--photo">
      <DemoPicture scene={scene} sizes={PHONE_SIZES} />
      <ThreadPill />
      <span className="figma-b__ph-count">{RECORDING_DEMO.countdown}</span>
      <span className="figma-b__ph-count-hint">{t("countdownHint")}</span>
    </div>
  );
}

/**
 * Step 3: recording in progress.
 *
 * @param {object} props
 * @param {import("./homepageDemo").DemoScene} props.scene - the photo behind it.
 */
function RecordingScreen({ scene }) {
  const t = useTranslations("web.homeB.scene.phone");
  return (
    <div className="figma-b__ph figma-b__ph--photo">
      <DemoPicture scene={scene} sizes={PHONE_SIZES} />
      <ThreadPill />
      <span className="figma-b__ph-timer">
        <span className="figma-b__ph-rec-dot" />
        {RECORDING_DEMO.timer}
      </span>
      <span className="figma-b__ph-stop">
        <span className="figma-b__ph-stop-square" />
      </span>
      <span className="figma-b__ph-private">{t("private")}</span>
    </div>
  );
}

/** Step 4: the read-back, drawn with no photo. */
function HeardScreen() {
  const t = useTranslations("web.homeB.scene.phone");
  return (
    <div className="figma-b__ph figma-b__ph--heard">
      <div className="figma-b__ph-heard-orb" />
      <div className="figma-b__ph-heard-title">{t("heardTitle")}</div>
      <div className="figma-b__ph-heard-card">
        <div className="figma-b__ph-heard-lead">{t("heardLead")}</div>
        <div className="figma-b__ph-heard-body">{t("heardBody")}</div>
      </div>
      <div className="figma-b__ph-heard-actions">
        <span className="figma-b__ph-keep">{t("keepTalking")}</span>
        <span className="figma-b__ph-continue">{t("continue")}</span>
      </div>
    </div>
  );
}

/**
 * The phone screen one step shows.
 *
 * @param {object} props
 * @param {import("./homepageDemo").StepperStep} props.step - the step.
 */
function PhoneScreen({ step }) {
  switch (step.screen) {
    case "prompt":
      return <PromptScreen />;
    case "countdown":
      return <CountdownScreen scene={step.scene} />;
    case "recording":
      return <RecordingScreen scene={step.scene} />;
    default:
      return <HeardScreen />;
  }
}

/**
 * Put the stepper in motion. Called once it has mounted, only where motion is
 * wanted and IntersectionObserver exists.
 *
 * @param {HTMLElement} group - the stepper's root.
 * @returns {() => void} undoes everything, leaving the still state.
 */
export function animateStepper(group) {
  const steps = [...group.querySelectorAll("[data-step]")];
  const dots = steps.map((step) => step.querySelector("[data-step-dot]"));
  const zigzag = window.matchMedia(`(min-width: ${STEPPER_ZIGZAG_MIN_WIDTH}px)`);
  group.setAttribute("data-animate", "");

  const reveal = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.setAttribute("data-seen", "");
        reveal.unobserve(entry.target);
      }
    },
    { threshold: REVEAL_THRESHOLD },
  );
  for (const step of steps) reveal.observe(step);

  let frame = 0;
  const update = () => {
    frame = 0;
    const box = group.getBoundingClientRect();
    const rail = railProgress(box.top, box.height, window.innerHeight);
    group.style.setProperty("--figma-b-rail", `${rail}%`);
    const layout = zigzag.matches ? "zigzag" : "vertical";
    dots.forEach((dot, index) => {
      dot?.toggleAttribute("data-reached", rail >= stepReachedAt(index, layout));
    });
  };
  const schedule = () => {
    if (frame === 0) frame = window.requestAnimationFrame(update);
  };

  let listening = false;
  const listen = (on) => {
    if (on === listening) return;
    listening = on;
    if (on) {
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule);
    } else {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    }
  };
  // The scroll listener exists only while the stepper is on screen; one last
  // update on the way out settles the rail at empty or full.
  const presence = new IntersectionObserver((entries) => {
    listen(entries.some((entry) => entry.isIntersecting));
    schedule();
  });
  presence.observe(group);
  update();

  return () => {
    reveal.disconnect();
    presence.disconnect();
    listen(false);
    if (frame !== 0) window.cancelAnimationFrame(frame);
    group.removeAttribute("data-animate");
    group.style.removeProperty("--figma-b-rail");
  };
}

/** Whether this browser should get the stepper's motion. */
function wantsMotion() {
  if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return false;
  return !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Strip B.
 *
 * @param {object} props
 * @param {string} props.ctaLabel - the CTA's label, as arm A words its primary CTA.
 * @param {() => void} props.onCta - called when the CTA is clicked.
 */
const StripStepper = ({ ctaLabel, onCta }) => {
  const t = useTranslations("web.homeB.stripB");
  const groupRef = useRef(null);

  useEffect(() => {
    const group = groupRef.current;
    if (group === null || !wantsMotion()) return undefined;
    return animateStepper(group);
  }, []);

  return (
    <section
      className="figma-a__section figma-a__section--muted figma-b__strip figma-b__strip--stepper"
      data-test-id="landing-strip-b"
    >
      <div className="figma-a__container">
        <div className="figma-b__header">
          <span className="figma-a__eyebrow">{t("eyebrow")}</span>
          <h2 className="figma-a__section-title">{t.rich("title", { accent })}</h2>
          <p className="figma-a__section-body">{t("body")}</p>
        </div>
        <div
          ref={groupRef}
          className="figma-b__stepper"
          role="group"
          aria-label={t("groupLabel")}
          data-test-id="landing-strip-b-stepper"
        >
          <div className="figma-b__rail" aria-hidden="true">
            <div className="figma-b__rail-fill" />
          </div>
          {STEPPER_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`figma-b__step figma-b__step--${index % 2 === 0 ? "phone-left" : "phone-right"}`}
              data-step={step.id}
            >
              <span className="figma-b__dot" aria-hidden="true" data-step-dot="">
                {index + 1}
              </span>
              <div className="figma-b__step-phone" aria-hidden="true">
                <PhoneScreen step={step} />
              </div>
              <div className="figma-b__step-caption">
                <h3 className="figma-b__step-title">{t(`steps.${step.id}.title`)}</h3>
                <p className="figma-b__step-body">{t(`steps.${step.id}.body`)}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="figma-b__caption figma-b__caption--stepper">{t("caption")}</p>
        <div className="figma-b__cta">
          <button
            className="figma-a__button"
            type="button"
            data-test-id="landing-cta-strip-b"
            onClick={onCta}
          >
            {ctaLabel}
          </button>
          <span className="figma-b__trust">{t("trust")}</span>
        </div>
      </div>
    </section>
  );
};

export default StripStepper;
